import { useFormContext } from 'react-hook-form';

import { Checkbox } from '@paid-ui/components/checkbox';
import { FormControl, FormField, FormItem } from '@paid-ui/components/form';
import { UserConsentId } from '@paid-ui/enums/consent';
import { openUserConsentDialog } from '@paid-ui/models/dialog';

interface Props {
  hidden?: boolean;
  disabled?: boolean;
}

// A <button> is an unbreakable inline-level box, so a long document name could
// not wrap mid-label and was pushed onto a line of its own. A span flows as
// ordinary text; preventDefault stops the click from also toggling the
// surrounding label's checkbox, which a real button would have suppressed.
const ConsentLink: React.FC<{ consentId: UserConsentId; children: React.ReactNode }> = ({
  consentId,
  children,
}) => (
  <span
    role="link"
    tabIndex={-1}
    onClick={(event) => {
      event.preventDefault();
      openUserConsentDialog(consentId);
    }}
    className="cursor-pointer text-blue hover:text-blue-dark"
  >
    {children}
  </span>
);

export const TermsCheckbox: React.FC<Props> = ({ hidden, disabled }) => {
  const form = useFormContext();

  if (hidden) {
    return null;
  }

  return (
    <FormField
      name="agreedOnTerms"
      control={form.control}
      render={({ field }) => (
        <FormItem className="text-sm text-grey">
          <FormControl>
            <Checkbox
              {...field}
              onValueChange={(value) =>
                form.setValue('agreedOnTerms', value, {
                  shouldValidate: true,
                })
              }
              disabled={disabled}
            >
              By creating an account you accept our{' '}
              <ConsentLink consentId={UserConsentId.SERVICE_AGREEMENT}>
                Service Agreement
              </ConsentLink>
              , <ConsentLink consentId={UserConsentId.PRIVACY_POLICY}>Privacy Policy</ConsentLink>,{' '}
              <ConsentLink consentId={UserConsentId.FINANCIAL_SERVICE_GUIDE}>
                Financial Services Guide
              </ConsentLink>
              ,{' '}
              <ConsentLink consentId={UserConsentId.PRODUCT_DISCLOSURE_STATEMENT}>
                Product Disclosure Statement
              </ConsentLink>
              ,{' '}
              <ConsentLink consentId={UserConsentId.MARKET_DETERMINATION}>
                Target Market Determination
              </ConsentLink>{' '}
              and{' '}
              <ConsentLink consentId={UserConsentId.TMD_SAVINGS_ACCOUNT}>
                Target Market Determination Paid Savings Account
              </ConsentLink>
            </Checkbox>
          </FormControl>
        </FormItem>
      )}
    />
  );
};
